import { getAuthToken } from "../../utils/getAuthToken";
import { performLogout } from "../../utils/logoutHandler";

const BASE_URL = process.env.REACT_APP_API_BASE_URL || ""; // env based switch
//console.log(BASE_URL)

// Flag to prevent multiple simultaneous 401 handling attempts
let isHandling401 = false;

// For authenticated endpoints
export interface ApiFetchOptions extends RequestInit {
  skipArtistId?: boolean; // Option to skip X-Artist-Id header (e.g., for confirm-details)
}

export const apiFetch = async (
  path: string,
  options: ApiFetchOptions = {},
): Promise<any> => {
  let token: string;
  
  try {
    token = await getAuthToken();
  } catch (error: any) {
    // Handle token errors (expired/invalid token) before making API call
    if (error?.isUnauthorized || error?.status === 401) {
      console.error('❌ Authentication token error:', error);
      
      // Only handle 401 once, even if multiple API calls fail simultaneously
      if (!isHandling401) {
        isHandling401 = true;
        
        // Use centralized logout handler (prevents race conditions)
        performLogout("Token expired/invalid during API call").finally(() => {
          // Reset flag after a delay to allow logout to complete
          setTimeout(() => {
            isHandling401 = false;
          }, 2000);
        });
      }
      
      // Re-throw the error so the caller knows the request failed
      throw error;
    }
    
    // Re-throw other token errors
    throw error;
  }

  // Log authentication details for debugging
  if (!token) {
    console.error('❌ No authentication token available!');
  }

  // Get current artist ID from localStorage (set by ArtistContext)
  // Add retry logic to handle race conditions where localStorage might not be set yet
  let currentArtistId = localStorage.getItem('currentArtistId');
  
  // If currentArtistId is not found, wait briefly and retry (handles race conditions)
  if (!currentArtistId) {
    // Check if this is a path that requires artist ID
    const requiresArtistId = path.includes('/analytics/') || 
                             path.includes('/users/getAccountBalance') ||
                             path.includes('/unitco/');
    
    if (requiresArtistId) {
      // Wait 50ms and retry once (allows ArtistContext to set localStorage)
      await new Promise(resolve => setTimeout(resolve, 50));
      currentArtistId = localStorage.getItem('currentArtistId');
      
      if (!currentArtistId) {
        console.warn('[apiFetch] No X-Artist-Id header after retry - currentArtistId is null for path:', path);
      }
    }
  }
  
  // Convert options.headers to plain object if it's a Headers object
  let existingHeaders: Record<string, string> = {};
  if (options.headers) {
    if (options.headers instanceof Headers) {
      options.headers.forEach((value, key) => {
        existingHeaders[key] = value;
      });
    } else if (typeof options.headers === 'object') {
      existingHeaders = options.headers as Record<string, string>;
    }
  }
  
  // Build headers - ensure X-Artist-Id is always included if available
  const headers: Record<string, string> = {
    ...existingHeaders,
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
  
  // Add X-Artist-Id header if artist is selected and skipArtistId is not true
  // skipArtistId is used for confirm-details updates (account setup, not artist-scoped)
  if (currentArtistId && !options.skipArtistId) {
    headers["X-Artist-Id"] = currentArtistId;
    console.log('[apiFetch] Adding X-Artist-Id header:', currentArtistId, 'for path:', path);
  } else if (options.skipArtistId) {
    console.log('[apiFetch] Skipping X-Artist-Id header (skipArtistId=true) for path:', path);
  } else {
    console.warn('[apiFetch] No X-Artist-Id header - currentArtistId is null for path:', path);
  }

  // Remove headers and skipArtistId from options to prevent override
  const { headers: _, skipArtistId: __, ...restOptions } = options;
  
  const res = await fetch(`${BASE_URL}${path}`, {
    ...restOptions,
    headers,
  });

  const data = await res.json();
  if (!res.ok) {
    // Check if this is an artist-scoped request (has X-Artist-Id header)
    const isArtistScoped = !!currentArtistId;
    
    console.error(`❌ API Error (${res.status}):`, {
      path,
      status: res.status,
      statusText: res.statusText,
      data,
      isArtistScoped,
      // Log original error details for debugging (not exposed to user)
      originalError: data?.error?.message
    });
    
    // Handle 500 errors (database connection issues) - don't logout, just show error
    if (res.status === 500 && data?.error?.type === 'database_error') {
      const error = new Error(data?.error?.message || "Database connection error. Please try again.");
      (error as any).status = 500;
      (error as any).isDatabaseError = true;
      throw error;
    }
    
    // Handle 401 Unauthorized errors specifically (real auth failures)
    if (res.status === 401) {
      // Only handle 401 once, even if multiple API calls fail simultaneously
      if (!isHandling401) {
        isHandling401 = true;
        
        // Use centralized logout handler (prevents race conditions)
        performLogout("API returned 401 Unauthorized").finally(() => {
          // Reset flag after a delay to allow logout to complete
          setTimeout(() => {
            isHandling401 = false;
          }, 2000);
        });
      }
      
      const error = new Error(data?.error?.message || "Unauthorized. Please login again.");
      (error as any).status = 401;
      (error as any).isUnauthorized = true;
      throw error;
    }
    
    // Handle missing required header errors specifically
    if (data?.error?.message?.includes('Missing required header') || 
        data?.error?.message?.includes('x-artist-id') ||
        data?.error?.message?.includes('X-Artist-Id')) {
      const error = new Error("Artist not selected. Please select an artist and try again.");
      (error as any).status = res.status;
      (error as any).code = data?.error?.code;
      (error as any).isMissingArtistId = true;
      throw error;
    }
    
    // Handle 403/404 from artist-scoped endpoints - show generic "No access" to prevent resource existence leakage
    if ((res.status === 403 || res.status === 404) && isArtistScoped) {
      const error = new Error("No access");
      (error as any).status = res.status;
      (error as any).code = data?.error?.code;
      (error as any).isAccessDenied = true;
      // Preserve original error details for internal logging (not exposed to user)
      (error as any).originalMessage = data?.error?.message;
      throw error;
    }
    
    // Preserve status code and error details for all other errors
    const error = new Error(data?.error?.message || "API request failed");
    (error as any).status = res.status;
    (error as any).code = data?.error?.code;
    // Attach full response data to error for service layer to check success flag
    (error as any).responseData = data;
    throw error;
  }

  return data;
};

// For public endpoints that don't require authentication
export const publicApiFetch = async (
  path: string,
  options: RequestInit = {},
): Promise<any> => {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      ...(options.headers || {}),
      "Content-Type": "application/json",
    },
  });

  const data = await res.json();
  if (!res.ok) {
    // Preserve status code and error details for all errors
    const error = new Error(data?.error?.message || "API request failed");
    (error as any).status = res.status;
    (error as any).code = data?.error?.code;
    throw error;
  }

  return data;
};
